/**
 * Reportes Ventas Generales — migración web de ares9630.
 *
 * Dos reportes en pestañas:
 *   - Ventas: contado (importe = valor) o crédito (importe = balance
 *     pendiente, con antigüedad de saldo 0-30/31-60/61-90/+90 calculada
 *     por el backend — el escritorio inflaba los totales del aging).
 *   - Pagos: cobros de pagosgps con filtros de categoría y forma de pago
 *     (el escritorio usaba las fechas de la pestaña equivocada; aquí no).
 * Filtros de categoría y sucursal opcionales. Exporta a Excel y PDF, e imprime.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(empresa, titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ compania: empresa, titulo, subtitulo, cabeceras, filas, totales });
}

export default function ReportesVentasGenerales() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState(null);

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [tipofactura, setTipofactura] = useState("Contado");
  const [categoria, setCategoria] = useState("");
  const [sucursal, setSucursal] = useState("");
  const [formapago, setFormapago] = useState("");

  const [ventas, setVentas] = useState([]);
  const [aging, setAging] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/ventageneral_config", {}).then(({ ok, data }) => {
      if (ok) {
        setConfig(data);
        // Sin alcance multi-sucursal (admin o permiso `versucursales`) el
        // usuario queda fijado a su sucursal.
        if (!data.versucursales && data.sucursal) setSucursal(String(data.sucursal));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      if (tab === 0) {
        const { ok, data } = await post("/api/ventageneral_reporte", {
          fechadesde: fecha1,
          fechahasta: fecha2,
          tipofactura,
          categoria,
          sucursal,
        });
        if (ok) {
          setVentas(data.ventas || []);
          setAging(data.aging || null);
          setTotal(data.total || 0);
        } else {
          showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
        }
      } else {
        const { ok, data } = await post("/api/ventageneral_reportepagos", {
          fechadesde: fecha1,
          fechahasta: fecha2,
          categoria,
          formapago,
          sucursal,
        });
        if (ok) {
          setPagos((data.pagos || []).map((r) => ({ ...r, id: r.norecibo })));
          setTotal(data.total || 0);
        } else {
          showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const esCredito = tab === 0 && tipofactura === "Credito";
  const subtitulo = `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}${
    categoria ? ` · ${categoria}` : ""
  }${sucursal ? ` · Sucursal ${sucursal}` : ""}`;

  const exportar = () => {
    const stamp = hoyISO();
    let hoja;
    if (tab === 0) {
      hoja = XLSX.utils.aoa_to_sheet([
        [`Ventas Generales ${tipofactura}`],
        [subtitulo],
        [],
        ["Fact#", "Fecha", "GPS", "Nombres", "Cédula", "Categoría", "Sucursal", "Importe"].concat(
          esCredito ? ["Días", "0-30", "31-60", "61-90", "+90"] : []
        ),
        ...ventas.map((v) =>
          [v.id, v.fecha, v.gps, v.nombres, v.cedula, v.tipogasto, v.sucursal, v.importe].concat(
            esCredito ? [v.dias, v.t0a30, v.t31a60, v.t61a90, v.tmas90] : []
          )
        ),
        [],
        ["", "", "", "", "", "", "TOTAL", total].concat(
          esCredito && aging ? ["", aging.t0a30, aging.t31a60, aging.t61a90, aging.tmas90] : []
        ),
      ]);
    } else {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Pagos Ventas Generales"],
        [subtitulo + (formapago ? ` · ${formapago}` : "")],
        [],
        ["Recibo#", "Préstamo", "Fecha", "Categoría", "Nombres", "Cédula", "Forma", "Valor"],
        ...pagos.map((p) => [
          p.norecibo,
          p.noprest,
          p.fecha,
          p.tipogasto,
          p.nombres,
          p.cedula,
          p.banco,
          p.vpagcap,
        ]),
        [],
        ["", "", "", "", "", "", "TOTAL", total],
      ]);
    }
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, tab === 0 ? "Ventas Generales" : "Pagos");
    XLSX.writeFile(libro, `ReporteVentasGenerales_${stamp}.xlsx`);
  };

  // Mismo contenido para la impresión y el PDF.
  const datosListado = () => {
    if (tab === 0) {
      return {
        titulo: `Ventas Generales ${tipofactura}`,
        subtitulo,
        cabeceras: ["Fact#", "Fecha", "Nombres", "Cédula", "Categoría", "Suc.", "Importe"].concat(
          esCredito ? ["0-30", "31-60", "61-90", "+90"] : []
        ),
        filas: ventas.map((v) =>
          [v.id, v.fecha, v.nombres, v.cedula, v.tipogasto, v.sucursal, fmtMoney(v.importe)].concat(
            esCredito
              ? [fmtMoney(v.t0a30), fmtMoney(v.t31a60), fmtMoney(v.t61a90), fmtMoney(v.tmas90)]
              : []
          )
        ),
        totales: ["", "", "", "", "", "TOTAL", fmtMoney(total)].concat(
          esCredito && aging
            ? [
                fmtMoney(aging.t0a30),
                fmtMoney(aging.t31a60),
                fmtMoney(aging.t61a90),
                fmtMoney(aging.tmas90),
              ]
            : []
        ),
        // Desde "Importe" en adelante son montos.
        desdeMonto: 6,
      };
    }
    return {
      titulo: "Pagos Ventas Generales",
      subtitulo: subtitulo + (formapago ? ` · ${formapago}` : ""),
      cabeceras: ["Recibo#", "Fecha", "Categoría", "Nombres", "Forma", "Valor"],
      filas: pagos.map((p) => [
        p.norecibo,
        p.fecha,
        p.tipogasto,
        p.nombres,
        p.banco,
        fmtMoney(p.vpagcap),
      ]),
      totales: ["", "", "", "", "TOTAL", fmtMoney(total)],
      desdeMonto: 5,
    };
  };

  const compania = () => config?.compania?.nombre || localStorage.getItem("nombrenegocio") || "";

  const imprimir = () => {
    const { titulo, subtitulo: sub, cabeceras, filas, totales } = datosListado();
    imprimirListado(compania(), titulo, sub, cabeceras, filas, totales);
  };

  const generarPdf = () => {
    const { titulo, subtitulo: sub, cabeceras, filas, totales, desdeMonto } = datosListado();
    // Crédito lleva las cuatro columnas de antigüedad: en horizontal caben.
    // eslint-disable-next-line new-cap
    const doc = new jsPDF({
      orientation: esCredito ? "landscape" : "portrait",
      unit: "mm",
      format: "letter",
    });
    const ancho = doc.internal.pageSize.getWidth();
    const columnasMonto = {};
    for (let i = desdeMonto; i < cabeceras.length; i += 1) {
      columnasMonto[i] = { halign: "right" };
    }

    autoTable(doc, {
      head: [cabeceras],
      body: filas,
      foot: [totales],
      showFoot: "lastPage",
      startY: 30,
      margin: { top: 30, left: 10, right: 10, bottom: 14 },
      styles: { font: "helvetica", fontSize: 8, cellPadding: 1.4, overflow: "linebreak" },
      headStyles: { fillColor: [27, 42, 73], textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: [236, 239, 244], textColor: 20, fontStyle: "bold" },
      columnStyles: columnasMonto,
      didParseCell: ({ section, column, cell }) => {
        if ((section === "head" || section === "foot") && column.index >= desdeMonto) {
          Object.assign(cell.styles, { halign: "right" });
        }
      },
      didDrawPage: () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text(compania(), ancho / 2, 12, { align: "center" });
        doc.setFontSize(11);
        doc.text(titulo, ancho / 2, 19, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.text(sub, ancho / 2, 25, { align: "center" });
      },
    });

    // Numeración al final, cuando ya se conoce el total de páginas.
    const paginas = doc.getNumberOfPages();
    const alto = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= paginas; i += 1) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(`Generado el ${fechaDMY(hoyISO())}`, 10, alto - 7);
      doc.text(`Página ${i} de ${paginas}`, ancho - 10, alto - 7, { align: "right" });
    }

    const nombre = tab === 0 ? `VentasGenerales${tipofactura}` : "PagosVentasGenerales";
    doc.save(`${nombre}_${fecha1}_${fecha2}.pdf`);
  };

  const columnasVentas = [
    { field: "id", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 170 },
    { field: "cedula", headerName: "Cédula", width: 135 },
    { field: "tipogasto", headerName: "Categoría", width: 120 },
    { field: "sucursal", headerName: "Suc.", width: 70 },
    {
      field: "importe",
      headerName: esCredito ? "Balance" : "Valor",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    ...(esCredito
      ? [
          { field: "dias", headerName: "Días", width: 70, align: "right", headerAlign: "right" },
          {
            field: "t0a30",
            headerName: "0-30",
            width: 100,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) => fmtMoney(value),
          },
          {
            field: "t31a60",
            headerName: "31-60",
            width: 100,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) => fmtMoney(value),
          },
          {
            field: "t61a90",
            headerName: "61-90",
            width: 100,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) => fmtMoney(value),
          },
          {
            field: "tmas90",
            headerName: "+90",
            width: 100,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) => fmtMoney(value),
          },
        ]
      : []),
  ];

  const columnasPagos = [
    { field: "norecibo", headerName: "Recibo#", width: 90 },
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "tipogasto", headerName: "Categoría", width: 125 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 170 },
    { field: "cedula", headerName: "Cédula", width: 135 },
    { field: "banco", headerName: "Forma", width: 110 },
    {
      field: "vpagcap",
      headerName: "Valor",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  const hayDatos = tab === 0 ? ventas.length > 0 : pagos.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reportes Ventas Generales"
        subtitle="Ventas al contado, balances a crédito con antigüedad de saldo y cobros aplicados"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              setTotal(0);
            }}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label="Ventas Generales" />
            <Tab label="Pagos Generales" />
          </Tabs>

          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            {tab === 0 ? (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de venta"
                  value={tipofactura}
                  onChange={(e) => setTipofactura(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="Contado">Contado</MenuItem>
                  <MenuItem value="Credito">Crédito</MenuItem>
                </TextField>
              </Grid>
            ) : (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Forma de pago"
                  value={formapago}
                  onChange={(e) => setFormapago(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="Contado">Contado</MenuItem>
                  <MenuItem value="Transferencia">Transferencia</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Categoría"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {(config?.categorias || []).map((c) => (
                  <MenuItem key={c.id} value={c.categoria}>
                    {c.categoria}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                {config?.versucursales !== false && <MenuItem value="">Todas</MenuItem>}
                {(config?.sucursales || [])
                  .filter((s) => config?.versucursales !== false || s.sucursal === config?.sucursal)
                  .map((s) => (
                    <MenuItem key={s.sucursal} value={s.sucursal}>
                      {s.sucursal}-{s.descripcion}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip
              label={tab === 0 ? (esCredito ? "BALANCE TOTAL" : "TOTAL VENDIDO") : "TOTAL COBRADO"}
              value={`RD$ ${fmtMoney(total)}`}
            />
            {esCredito && aging && (
              <>
                <ResumenChip
                  label="0-30 DÍAS"
                  value={`RD$ ${fmtMoney(aging.t0a30)}`}
                  color="#2E7D32"
                />
                <ResumenChip
                  label="31-60 DÍAS"
                  value={`RD$ ${fmtMoney(aging.t31a60)}`}
                  color="#F9A825"
                />
                <ResumenChip
                  label="61-90 DÍAS"
                  value={`RD$ ${fmtMoney(aging.t61a90)}`}
                  color="#EF6C00"
                />
                <ResumenChip
                  label="+90 DÍAS"
                  value={`RD$ ${fmtMoney(aging.tmas90)}`}
                  color="#C62828"
                />
              </>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PictureAsPdfOutlinedIcon />}
              onClick={generarPdf}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.danger)}
            >
              PDF
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={tab === 0 ? ventas : pagos}
              columns={tab === 0 ? columnasVentas : columnasPagos}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
